import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { InputBase, Link, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { SearchIcon, Icon } from 'packages/eid-icons'
import { useDebounce } from 'packages/core'
import SelectMenu from './SelectMenu'

const SearchMenuRoot = styled('div')({
    display: 'flex',
    width: '100%',
})

const SearchContainer = styled('div')(({ theme, bgColor, borderColor }) => ({
    width: '100%',
    height: '3.6rem',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    //borderRadius: 3,
    borderTopLeftRadius: '0.3rem',
    borderBottomLeftRadius: '0.3rem',
    borderRight: '0px',
    margin: 'auto',
    border: `0.1rem solid ${borderColor}`,
    backgroundColor: bgColor,
}))

const SearchIconWrapper = styled('div')({
    width: '1.3rem',
    height: '1.3rem',
    marginLeft: '1.7rem',
})

const CloseIconWrapper = styled(Link)({
    width: '1.3rem',
    height: '1.3rem',
    marginRight: '1.7rem',
    paddingTop: '0.1rem',
    zIndex: 10,
})

const StyledInputBase = styled(InputBase)(({ theme, color, hoverColor }) => ({
    padding: '1rem 0 0.8rem 0',
    marginLeft: '0.8rem',
    fontSize: '1.3rem',
    flex: 1,
    color: `${color} !important`,
    '&:focus': {
        color: `${hoverColor} !important`,
    },
}))

function SearchMenu({
    handleSearch,
    handleSelect,
    styleData,
    placeholder,
    value,
    options,
}) {
    const { t } = useTranslation()
    const [searchKey, setSearchKey] = useState(value)

    useEffect(() => {
        if (value !== searchKey) {
            setSearchKey(value)
        }
    }, [value])

    const handleChange = (event) => {
        const keyword = event.target.value
        setSearchKey(keyword)
    }
    const handleClearSearch = () => {
        setSearchKey('')
    }
    const debouncedValue = useDebounce(searchKey)

    useEffect(() => {
        handleSearch(debouncedValue)
    }, [debouncedValue, handleSearch])

    return (
        <SearchMenuRoot>
            <SearchContainer
                bgColor={styleData.bgColor}
                borderColor={styleData.borderColor}
            >
                <SearchIconWrapper>
                    <SearchIcon color={styleData.iconColor} />
                </SearchIconWrapper>
                <StyledInputBase
                    fullWidth={true}
                    value={searchKey}
                    onChange={handleChange}
                    placeholder={placeholder ?? 'Search'}
                    inputProps={{
                        'aria-label': placeholder ?? 'Search',
                    }}
                    color={styleData.color}
                    hoverColor={styleData.hoverColor}
                />
                {searchKey && (
                    <CloseIconWrapper
                        component="button"
                        onClick={() => handleClearSearch()}
                        aria-label="Clear"
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                handleClearSearch()
                            }
                        }}
                    >
                        <Icon name="Close" color={styleData.iconColor} />
                    </CloseIconWrapper>
                )}
            </SearchContainer>
            <SelectMenu
                options={options}
                handleSelect={handleSelect}
                inputStyles={{
                    minWidth: '12rem',
                    margin: 'auto',
                    border: `0.1rem solid ${styleData.borderColor}`,
                    backgroundColor: styleData.bgColor,
                    borderBottomRightRadius: '0.3rem',
                    borderTopRightRadius: '0.3rem',
                    width: '11.5rem',
                    color: styleData.color,
                }}
            />
        </SearchMenuRoot>
    )
}

SearchMenu.propTypes = {
    handleSearch: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
}

SearchMenu.defaultProps = {
    value: '',
    styleData: {
        bgColor: '#f7f8fa',
        borderColor: '#ebebed',
        iconColor: '#b5b6b8',
        color: '#a7a7a9',
        hoverColor: '#a7a7a9',
    },
}

export default SearchMenu
